import React, { useState } from 'react';
import CryptoJS from 'crypto-js';
import { DillButton, DillModal } from '../../../general/Dill';
import { firebaseLargeFileUploadHelper, openDillToast } from '../../../../utils/helpers';
import { BankReference } from '@dill/dill-shared';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { UploadFileInputs } from './UploadFileInput';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getSupplierCreditApplication,
  updateBankReferenceResponse,
} from '../../../../redux/services/creditApplicationsService';

const BankReferenceResponseModal = ({
  open,
  handleClose,
  bankReference,
  creditApplicationId,
  applicationHistoryId,
}: {
  open: boolean;
  handleClose: () => void;
  bankReference: BankReference;
  creditApplicationId: string;
  applicationHistoryId?: string | null;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((globalState) => globalState.auth);
  const currentSupplier = useAppSelector((globalState) => globalState.auth.currentSupplier);
  const [loading, setLoading] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleCloseModal = () => {
    setLoading(false);
    setResponseText('');
    setSelectedFiles([]);
    handleClose();
  };

  const handleSubmit = async () => {
    if (!responseText.trim() && selectedFiles.length === 0) {
      openDillToast({
        message: 'Please provide either a bank reference text or upload at least one file.',
        type: 'ERROR',
      });
      return;
    }
    try {
      setLoading(true);
      const secretKey = process.env.REACT_APP_CRYPTO_KEY;

      if (!secretKey) {
        throw new Error('Missing encryption key');
      }

      const encryptedAccountNumber = CryptoJS.AES.encrypt(
        JSON.stringify(bankReference?.bankDetails?.accountNumber ?? ''),
        secretKey
      ).toString();

      let uploadedFiles: { url: string; name: string; fileType: string }[] = [];
      if (selectedFiles.length > 0) {
        const results = await Promise.all(
          selectedFiles.map(async (file) => {
            const fileType = file.name.split('.').pop() || '';
            const blobUrl = URL.createObjectURL(file);

            try {
              const uploadURL = await firebaseLargeFileUploadHelper({
                blobUrl,
                fileNameWithExtension: file.name,
                storagePath: `creditApplicationFiles/${currentSupplier?.id}/${Date.now()}`,
              });

              if (!uploadURL) return null;

              return { url: uploadURL, name: file.name, fileType };
            } finally {
              URL.revokeObjectURL(blobUrl);
            }
          })
        );
        uploadedFiles = results.filter(
          (file): file is { url: string; name: string; fileType: string } =>
            Boolean(file && file.url)
        );
      }

      const existingResponse = bankReference?.referenceResponse ?? {};

      const updatedBankReference = {
        ...bankReference,
        bankDetails: {
          ...bankReference.bankDetails,
          accountNumber: encryptedAccountNumber,
        },
        referenceResponse: {
          ...existingResponse,
          ...(responseText && { textResponse: responseText }),
          ...((uploadedFiles.length > 0 || existingResponse?.files) && {
            files: [...(existingResponse?.files ?? []), ...uploadedFiles],
          }),
        },
      };

      const result = await dispatch(
        updateBankReferenceResponse({
          supplierId: currentSupplier?.id ?? '',
          bankReference: updatedBankReference,
          creditApplicationId,
          ...(applicationHistoryId ? { applicationHistoryId } : {}),
        })
      );
      if (result.type.includes('fulfilled')) {
        openDillToast({
          type: 'SUCCESS',
          message: 'Succesfully update bank reference response.',
        });
        setLoading(false);
        dispatch(
          getSupplierCreditApplication({
            supplierId: currentSupplier?.id || '',
            creditApplicationId: creditApplicationId,
            hashedType: 'encrypted',
            fetchType: 'detailed',
            skipLoading: 'true',
          })
        );
        handleCloseModal();
      } else {
        openDillToast({
          type: 'ERROR',
          message: 'Failed to update bank reference response.',
        });
        setLoading(false);
      }
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message: error?.message || 'Failed to update bank reference response.',
      });
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setSelectedFiles((prev) =>
      prev.filter(
        (file) =>
          !(file.name === fileToRemove.name && file.lastModified === fileToRemove.lastModified)
      )
    );
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Bank Reference Response"
      footer={
        <div className="flex justify-end w-full gap-2">
          <DillButton label="Close" variant="ghost" onClick={handleCloseModal} />
          <DillButton
            label="Submit"
            onClick={handleSubmit}
            isLoading={loading}
            isDisabled={loading}
          />
        </div>
      }>
      <div className="flex flex-col gap-2">
        <AppInputField
          label="Bank Reference"
          id="bankReference"
          placeholder="Enter Bank Reference Response"
          inputType="textarea"
          value={responseText}
          onTextChange={setResponseText}
        />

        {selectedFiles.map((file, idx) => (
          <div
            key={`${file.name}-${file.lastModified}-${idx}`}
            className="flex gap-1 md:gap-2 items-center">
            <div className="text-xs">{file.name}</div>
            <button onClick={() => handleRemoveFile(file)} className="ERROR_500-CLR text-xs">
              Remove
            </button>
          </div>
        ))}

        <UploadFileInputs
          label=""
          selectedFile={null}
          onSelectFile={(data) => setSelectedFiles((prev) => [...prev, data.file])}
        />
      </div>
    </DillModal>
  );
};

export default BankReferenceResponseModal;
